import { Box, Paper } from '@mui/material';
import { ComputedDatum, Pie, PieLayer } from '@nivo/pie';
import useComponentSize from '@rehooks/component-size';
import { useRef, useState } from 'react';

import { PersonnelReportData } from '~/data/reports';
import { analyticsColors } from '~/scenes/PortalModules/Analytics/common/colors';

import { usePersonnelReportTables } from '../../usePersonnelReportTables';
import { ChartTable } from '../ChartTable/component';

import { useSxStyles } from './useStyles';

export type PieComputedDatum = ComputedDatum<{
  id: string;
  value: number;
}>;

interface DoughnutChartProps {
  data: { id: string; value: number }[];
  tableData?: Record<
    string,
    { count: number; tableData: PersonnelReportData[] }
  >;
  dataLength: number;
  dataLegend: { id: string; label: string; color: string }[];
  label: string;
  selectedSlice: PieComputedDatum;
  setSelectedSlice: (slice: PieComputedDatum | null) => void;
  omitColumns?: string[];
  exportFileNamePrefix?: string;
}

export const DoughnutChart = (props: DoughnutChartProps) => {
  const {
    data,
    tableData,
    dataLength,
    dataLegend,
    label,
    selectedSlice,
    setSelectedSlice,
    omitColumns,
    exportFileNamePrefix,
  } = props;

  const [tablePosition, setTablePosition] = useState<{
    top: number;
    left: number;
  }>();
  const [selectedPie, setSelectedPie] = useState<PieComputedDatum | null>(null);

  const sxStyles = useSxStyles();

  const { rows, columns } = usePersonnelReportTables(
    tableData?.[selectedPie?.id || '']?.tableData || [],
    omitColumns,
  );

  const chartRef = useRef();
  const componentSize = useComponentSize(chartRef);

  const commonPieChartsLayer = [
    'arcs',
    'arcLabels',
    'arcLinkLabels',
    'legends',
    ({ centerX, centerY }) => (
      <text x={centerX} y={centerY} dy={4} textAnchor="middle" fontSize={14}>
        {dataLength || 0}
      </text>
    ),
  ] as PieLayer<{ id: string; value: number }>[] | undefined;

  return (
    <Paper sx={sxStyles.container}>
      <Box component="h4" sx={sxStyles.title}>
        {label}
      </Box>

      <Box sx={sxStyles.chart} ref={chartRef}>
        <Pie
          legends={[
            {
              anchor: 'bottom',
              direction: 'row',
              justify: false,
              translateX: 0,
              translateY: 40,
              itemsSpacing: 0,
              itemWidth: 50,
              itemHeight: 18,
              itemTextColor: '#999',
              itemDirection: 'top-to-bottom',
              symbolSize: 18,
              symbolShape: 'square',
              data: dataLegend,
            },
          ]}
          data={data}
          colors={analyticsColors}
          innerRadius={0.5}
          width={componentSize.width > 300 ? componentSize.width : 300}
          height={300}
          margin={{ top: 60, right: 60, bottom: 60, left: 60 }}
          borderWidth={2}
          borderColor="#ffffff"
          enableArcLinkLabels={false}
          layers={commonPieChartsLayer}
          onClick={(pie, { clientX, clientY }) => {
            if (pie.id === selectedSlice?.id) {
              setSelectedSlice(null);
              setSelectedPie(null);
            } else {
              const layoutAlignmentX =
                window.innerWidth - clientX < 430 ? -420 : 0;
              setTablePosition({
                top: clientY,
                left: layoutAlignmentX + clientX,
              });
              setSelectedSlice(pie);
              setSelectedPie(pie);
            }
          }}
        />
      </Box>

      <ChartTable
        isVisible={
          !!selectedSlice?.arc &&
          selectedSlice?.data.id === selectedPie?.data.id
        }
        tablePosition={tablePosition}
        columns={columns}
        rows={rows}
        label={selectedSlice?.id || ''}
        onClose={() => {
          setSelectedSlice(null);
          setSelectedPie(null);
        }}
        exportFileNamePrefix={exportFileNamePrefix}
      />
    </Paper>
  );
};
